/**
 * ### Scene layout
 *
 * Positions the moving parts have to agree on: where the microphone hangs,
 * where the picker stands, where the server sits. Kept out of the drawing
 * components so the timing of the pick and of the server's verdict can be
 * derived from the same numbers the parts are drawn with. The scene is centred on screen, so the story is read left to right
 * from the middle: hear, pick, judge.
 */

/** The drawing's own coordinate space, which the viewBox and every scale-dependent stroke agree on. */
export const SCENE = { width: 1200, height: 480 } as const;

/**
 * ### BOX
 *
 * One unit as drawn: an iso cube standing with its bottom-front corner at
 * the origin, so a translate to `baseY` on the belt places it on the rail.
 * The footprint is a diamond `halfWidth` wide and `halfDepth` deep on each
 * side of that corner, and the turntable it stops on is centred on the
 * footprint, not the corner.
 */
export const BOX = {
  halfWidth: 28,
  halfDepth: 14,
  height: 40,
  baseY: 300,
} as const;

/** The boom stand as drawn, before the station offset. */
export const MIC = {
  poleX: 548,
  poleTop: 118,
  pivotY: 128,
  boomEndX: 612,
  boomEndY: 160,
} as const;

export interface Station {
  /** Stable identifier, used for keys and animation classes */
  id: 'main';
  /** Shift of the whole stand along the belt, in scene units */
  offsetX: number;
  /** Whether the stand is mirrored about its pole to face back up the belt */
  mirrored: boolean;
}

/**
 * The one pickup point. A single station keeps the eye on one place; the
 * stand is moved left of the drawing's centre so the picker and the server
 * that follow it balance the composition.
 */
export const STATIONS: ReadonlyArray<Station> = [
  { id: 'main', offsetX: -150, mirrored: false },
];

/**
 * ### capsuleX
 *
 * Scene x of a station's capsule swivel, which is where the box below is
 * heard and where the signal leaves.
 */
export function capsuleX({ offsetX, mirrored }: Station): number {
  return mirrored
    ? offsetX + 2 * MIC.poleX - MIC.boomEndX
    : offsetX + MIC.boomEndX;
}

/**
 * ### INSPECTION_X
 *
 * Scene x where every unit stops to be heard: directly under the capsule.
 * The travel keyframes, the turntable and the server's verdict are all timed
 * from this one point.
 */
export const INSPECTION_X = capsuleX(STATIONS[0]);

/**
 * ### INSPECTION_PLATE
 *
 * The lift-and-turn station set into the belt at the inspection point: a
 * disc that rises out of a well by `lift` to carry the unit clear of the
 * rail while it is turned, then settles flush again. It is centred on the
 * footprint of a unit stopped there, so the unit turns about the disc's
 * axis, and it is narrow enough that the unit queued behind stops short of
 * its rim.
 */
export const INSPECTION_PLATE = {
  x: INSPECTION_X,
  y: BOX.baseY - BOX.halfDepth,
  radiusX: 80,
  radiusY: 18,
  innerRadiusX: 56,
  innerRadiusY: 11,
  lift: 12,
} as const;

/**
 * The horizontal bounds around a station, relative to an unmirrored stand.
 * Wide enough to take in the whole stand and short of the picker on the
 * right; the frame is hung on its left edge on a narrow screen.
 */
export const INSPECTION_ZONE = {
  relativeX: 440,
  y: 60,
  width: 340,
  height: 320,
} as const;

/**
 * ### inspectionZoneX
 *
 * Left edge of a station's zone, mirrored about the pole with the stand so
 * the zone sits around the microphone whichever way it faces.
 */
export function inspectionZoneX({ offsetX, mirrored }: Station): number {
  return mirrored
    ? offsetX +
        2 * MIC.poleX -
        (INSPECTION_ZONE.relativeX + INSPECTION_ZONE.width)
    : offsetX + INSPECTION_ZONE.relativeX;
}

/**
 * The picker is drawn at its own coordinates and shifted as a whole, so the
 * joint origins its animation pivots on stay where they are drawn.
 */
export const ROBOT_OFFSET_X = -200;

/**
 * The picker's joint geometry, in scene units after {@link ROBOT_OFFSET_X}.
 * The gripper hangs down and to the left of the shoulder, so turning the
 * shoulder clockwise raises it almost straight up: that is the motion that
 * lifts a box off the line, and the numbers below are what the box's own
 * keyframes follow so the two stay together.
 */
export const ARM = {
  shoulder: { x: 890 + ROBOT_OFFSET_X, y: 196 },
  gripAtRest: { x: 796 + ROBOT_OFFSET_X, y: 236 },
  /** Degrees clockwise: hovering clear of the belt between picks */
  restAngle: 14,
  /** Down on the box, where the drawing itself places the gripper */
  grabAngle: 0,
  /** Up, clear of the line, holding the box */
  liftAngle: 45,
} as const;

/** Scene x under the gripper when the arm is down: where a defect is stopped */
export const PICK_X = ARM.gripAtRest.x;

/**
 * ### gripAt
 *
 * Where the gripper sits when the shoulder is turned `degrees` clockwise.
 * Rotating a point about the shoulder, in SVG's y-down frame.
 */
export function gripAt(degrees: number): { x: number; y: number } {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = ARM.gripAtRest.x - ARM.shoulder.x;
  const dy = ARM.gripAtRest.y - ARM.shoulder.y;

  return {
    x: ARM.shoulder.x + dx * cos - dy * sin,
    y: ARM.shoulder.y + dx * sin + dy * cos,
  };
}

/**
 * The analysis tower. It stands on the same floor the belt legs reach
 * (y 426), so it is not left floating between the belt and the ground, and
 * it is tall enough to be the end point the whole signal run leads to.
 */
export const SERVER = {
  x: 880,
  y: 112,
  width: 100,
  height: 314,
  depth: 30,
} as const;

/**
 * ### SCENE_FOCUS
 *
 * The share of the scene's width that the story sits at the middle of: from
 * the left edge of the inspection zone to the far face of the server, which
 * is hear, pick and judge and nothing else. The belt runs past both of them
 * to the edges of any screen and needs no room reserved for it.
 *
 * A screen narrow enough to crop the scene is anchored on this rather than
 * on the drawing's own centre. Centred, a phone keeps the microphone and
 * the picker and cuts the server off the right-hand side — which loses the
 * verdict, the one thing the whole scene is drawn to show.
 */
export const SCENE_FOCUS =
  (inspectionZoneX(STATIONS[0]) + (SERVER.x + SERVER.width + SERVER.depth)) /
  2 /
  SCENE.width;
